import { mediaUrl } from "@/lib/api";
import { DEFAULT_BRAND_NAME } from "@/lib/siteBrand";

export const DEFAULT_SEO = {
  title: "",
  description: "",
  keywords: "",
  siteUrl: "",
  canonicalUrl: "",
  ogTitle: "",
  ogDescription: "",
  ogImage: "",
  ogType: "website",
  ogLocale: "fa_IR",
  twitterCard: "summary_large_image",
  twitterSite: "",
  twitterCreator: "",
  robots: "index, follow",
  favicon: "",
  author: "",
  themeColor: "#000000",
  googleVerification: "",
  bingVerification: "",
  jsonLdEnabled: true,
};

export function mergeSeo(seo) {
  return { ...DEFAULT_SEO, ...(seo || {}) };
}

export function resolveAbsoluteUrl(url, base) {
  if (!url) return "";
  const normalized = mediaUrl(url);
  if (normalized.startsWith("data:") || normalized.startsWith("http://") || normalized.startsWith("https://")) {
    return normalized;
  }
  if (!base) return normalized;
  try {
    return new URL(normalized.startsWith("/") ? normalized : `/${normalized}`, base.replace(/\/$/, "") + "/").href;
  } catch {
    return normalized;
  }
}

export function buildJsonLd(settings, siteUrl) {
  const seo = mergeSeo(settings?.seo);
  const hero = settings?.hero || {};
  const contact = settings?.contact_info || {};
  const base = siteUrl || (typeof window !== "undefined" ? window.location.origin : "");

  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: hero.name || seo.author || seo.title || undefined,
    description: seo.description || hero.description || undefined,
    url: base || undefined,
    jobTitle: hero.subtitle || undefined,
  };

  const email = contact.email?.trim();
  if (email) data.email = email;

  const image = resolveAbsoluteUrl(seo.ogImage, base);
  if (image) data.image = image;

  return data;
}

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${CSS.escape(key)}"]`;
  if (content == null || content === "") {
    document.querySelector(selector)?.remove();
    return;
  }
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, extra = {}) {
  const selector = `link[rel="${CSS.escape(rel)}"]`;
  if (!href) {
    document.querySelector(selector)?.remove();
    return;
  }
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
}

function upsertJsonLd(id, data) {
  const scriptId = `seo-${id}`;
  let el = document.getElementById(scriptId);
  if (!data) {
    el?.remove();
    return;
  }
  const json = JSON.stringify(data);
  if (!el) {
    el = document.createElement("script");
    el.id = scriptId;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = json;
}

export function applySeo(settings, options = {}) {
  if (typeof document === "undefined") return;

  const { pathname = "/", noindex = false } = options;
  const seo = mergeSeo(settings?.seo);
  const siteUrl = (seo.siteUrl || window.location.origin).replace(/\/$/, "");
  const title = seo.title || DEFAULT_BRAND_NAME;
  const description = seo.description || "";
  const ogTitle = seo.ogTitle || title;
  const ogDescription = seo.ogDescription || description;
  const canonical = seo.canonicalUrl || resolveAbsoluteUrl(pathname || "/", siteUrl);
  const ogImage = resolveAbsoluteUrl(seo.ogImage, siteUrl);
  const favicon = resolveAbsoluteUrl(seo.favicon, siteUrl);

  document.title = title;
  const lang = (seo.ogLocale || "fa_IR").split("_")[0];
  document.documentElement.lang = lang;

  upsertMeta("name", "description", description);
  upsertMeta("name", "keywords", seo.keywords);
  upsertMeta("name", "author", seo.author);
  upsertMeta("name", "robots", noindex ? "noindex, nofollow" : seo.robots);
  upsertMeta("name", "theme-color", seo.themeColor);

  upsertMeta("property", "og:site_name", title.split("|")[0].trim());
  upsertMeta("property", "og:title", ogTitle);
  upsertMeta("property", "og:description", ogDescription);
  upsertMeta("property", "og:type", seo.ogType || "website");
  upsertMeta("property", "og:locale", seo.ogLocale || "fa_IR");
  upsertMeta("property", "og:url", canonical);
  if (ogImage) upsertMeta("property", "og:image", ogImage);

  upsertMeta("name", "twitter:card", seo.twitterCard || "summary_large_image");
  upsertMeta("name", "twitter:title", ogTitle);
  upsertMeta("name", "twitter:description", ogDescription);
  if (ogImage) upsertMeta("name", "twitter:image", ogImage);
  if (seo.twitterSite) upsertMeta("name", "twitter:site", seo.twitterSite);
  if (seo.twitterCreator) upsertMeta("name", "twitter:creator", seo.twitterCreator);

  if (seo.googleVerification) upsertMeta("name", "google-site-verification", seo.googleVerification);
  else upsertMeta("name", "google-site-verification", "");
  if (seo.bingVerification) upsertMeta("name", "msvalidate.01", seo.bingVerification);
  else upsertMeta("name", "msvalidate.01", "");

  upsertLink("canonical", canonical);
  upsertLink("icon", favicon);

  if (seo.jsonLdEnabled && !noindex) {
    upsertJsonLd("person", buildJsonLd(settings, siteUrl));
  } else {
    upsertJsonLd("person", null);
  }
}

export function seoPreviewData(seo, siteUrl) {
  const merged = mergeSeo(seo);
  const base = (siteUrl || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const title = merged.title || "عنوان صفحه";
  const description = merged.description || "توضیحات متا";
  return {
    title,
    description,
    ogTitle: merged.ogTitle || title,
    ogDescription: merged.ogDescription || description,
    ogImage: resolveAbsoluteUrl(merged.ogImage, base),
    canonical: merged.canonicalUrl || base || "/",
    robots: merged.robots,
  };
}
